import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useAdminUi } from "../../hooks/useAdminUi";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i;
const CAROUSEL_AUTO_MS = 4000;

function useCarouselIndex(slideCount, resetKey) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [resetKey]);

  useEffect(() => {
    if (slideCount <= 1 || paused) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slideCount);
    }, CAROUSEL_AUTO_MS);
    return () => window.clearInterval(id);
  }, [slideCount, paused, resetKey]);

  const go = (delta) => setIndex((i) => (i + delta + slideCount) % slideCount);

  return {
    index,
    setIndex,
    go,
    pause: () => setPaused(true),
    resume: () => setPaused(false),
  };
}

function formatPropertyPrice(price, currency) {
  if (price == null || price === "") return "—";
  const n = Number(price);
  if (!Number.isFinite(n)) return String(price);
  const code = currency && String(currency).trim();
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${n.toLocaleString()} ${code}`;
    }
  }
  return `$${n.toLocaleString()}`;
}

function formatAnnouncementStatus(item) {
  const s = item?.status;
  if (s === 1 || s === "1") return "Pending";
  if (s === 2 || s === "2") return "Active";
  if (s === 3 || s === "3") return "Rejected";
  if (s === 0 || s === "0") return "Draft";
  return "—";
}

function listingTypeLabel(n) {
  if (n === 1 || n === "1") return "Buyer listing";
  if (n === 2 || n === "2") return "Broker listing";
  return n != null && n !== "" ? `Type ${n}` : "—";
}

function formatDetailDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function collectGalleryImages(media) {
  const urls = [];
  if (media?.thumbnail) urls.push(media.thumbnail);
  if (Array.isArray(media?.images)) {
    media.images.forEach((url) => {
      if (url && !urls.includes(url)) urls.push(url);
    });
  }
  return urls;
}

function formatArea(size) {
  if (!size || typeof size !== "object") return "—";
  const parts = [];
  if (size.sqft != null) parts.push(`${Number(size.sqft).toLocaleString()} ft²`);
  if (size.sqm != null) parts.push(`${size.sqm} m²`);
  return parts.length ? parts.join(" · ") : "—";
}

function formatFloors(floor, totalFloors) {
  if (floor != null && totalFloors != null) return `${floor} / ${totalFloors}`;
  if (floor != null) return String(floor);
  if (totalFloors != null) return String(totalFloors);
  return "—";
}

function yearFromDate(iso) {
  if (!iso) return "—";
  try {
    return String(new Date(iso).getFullYear());
  } catch {
    return "—";
  }
}

function isImageUrl(url) {
  return typeof url === "string" && IMAGE_EXT.test(url);
}

function looksLikePdf(url) {
  return typeof url === "string" && /\.pdf(\?|$)/i.test(url);
}

/** Resolve file URL from string or nested API object */
function resolveFileUrl(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const s = value.trim();
    return s || null;
  }
  if (typeof value === "object") {
    const candidates = [
      value.frontUrl,
      value.backUrl,
      value.fileUrl,
      value.url,
      value.imageUrl,
      value.image,
      value.path,
      value.secure_url,
      value.href,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c.trim();
    }
  }
  return null;
}

function shouldPreviewAsImage(url) {
  if (!url || looksLikePdf(url)) return false;
  if (isImageUrl(url)) return true;
  return /^https?:\/\//i.test(url);
}

function buildDocSlots(docs) {
  if (!docs || typeof docs !== "object") {
    return [
      { id: "passport-front", label: "Passport (front)", url: null },
      { id: "passport-back", label: "Passport (back)", url: null },
      { id: "title-deed", label: "Title deed", url: null },
      { id: "noc", label: "NOC", url: null },
    ];
  }
  return [
    {
      id: "passport-front",
      label: "Passport (front)",
      url: resolveFileUrl(docs.passport?.frontUrl ?? docs.passport?.front ?? docs.passport),
    },
    {
      id: "passport-back",
      label: "Passport (back)",
      url: resolveFileUrl(docs.passport?.backUrl ?? docs.passport?.back),
    },
    {
      id: "title-deed",
      label: "Title deed",
      url: resolveFileUrl(docs.titleDeed?.fileUrl ?? docs.titleDeed?.url ?? docs.titleDeed),
    },
    {
      id: "noc",
      label: "NOC",
      url: resolveFileUrl(docs.noc?.fileUrl ?? docs.noc?.url ?? docs.noc),
    },
  ];
}

function GallerySlot({ src, alt, ui, sx, emptyLabel = "No image" }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: ui.panelMuted,
        border: `1px solid ${ui.borderLight}`,
        ...sx,
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: ui.inkSoft,
            fontSize: 11,
          }}
        >
          {emptyLabel}
        </Box>
      )}
    </Box>
  );
}

function DocumentCard({ label, url, ui }) {
  const [imgError, setImgError] = useState(false);
  const tryImage = url && shouldPreviewAsImage(url) && !imgError;

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${ui.borderLight}`,
        bgcolor: ui.panelMuted,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
      }}
    >
      <Box
        component={tryImage ? "a" : "div"}
        href={tryImage ? url : undefined}
        target={tryImage ? "_blank" : undefined}
        rel={tryImage ? "noopener noreferrer" : undefined}
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: ui.detailPanelBg,
          display: "block",
          overflow: "hidden",
          position: "relative",
          textDecoration: "none",
          cursor: tryImage ? "pointer" : "default",
        }}
      >
        {tryImage ? (
          <Box
            component="img"
            src={url}
            alt={label}
            onError={() => setImgError(true)}
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 100,
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : url ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 32, color: ui.inkSoft }} />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              minHeight: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: ui.inkSoft,
              fontSize: 11,
            }}
          >
            No file
          </Box>
        )}
      </Box>
      <Box sx={{ flexShrink: 0, px: 1, py: 0.75, borderTop: `1px solid ${ui.borderLight}`, bgcolor: ui.surface }}>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: ui.inkMuted,
            lineHeight: 1.25,
            mb: url ? 0.5 : 0,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {label}
        </Typography>
        {url ? (
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: ui.green,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.35,
              "&:hover": { color: ui.greenDark },
            }}
          >
            Download file
            <DownloadOutlinedIcon sx={{ fontSize: 13 }} />
          </Link>
        ) : null}
      </Box>
    </Box>
  );
}

function CollapsibleSection({ title, countLabel, defaultOpen = false, children, ui }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${ui.borderLight}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: ui.surface,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { bgcolor: ui.panelMuted },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: ui.ink }}>{title}</Typography>
          {countLabel ? (
            <Typography sx={{ fontSize: 12, color: ui.inkSoft, mt: 0.25 }}>{countLabel}</Typography>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label={open ? "Collapse documents" : "Expand documents"}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          sx={{ color: ui.inkMuted }}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
      <Collapse in={open}>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, pt: 0 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

function CarouselControls({ index, total, onPrev, onNext, onGoTo, onInteract, ui }) {
  if (total <= 1) {
    return total === 1 ? (
      <Typography sx={{ fontSize: 12, color: ui.inkSoft, textAlign: "center", mt: 1 }}>1 of 1</Typography>
    ) : null;
  }

  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        <IconButton
          size="small"
          aria-label="Previous slide"
          onClick={() => {
            onInteract?.();
            onPrev();
          }}
          sx={{
            border: `1px solid ${ui.borderLight}`,
            bgcolor: ui.surface,
            "&:hover": { bgcolor: ui.panelMuted },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: ui.inkMuted, minWidth: 48, textAlign: "center" }}>
          {index + 1} / {total}
        </Typography>
        <IconButton
          size="small"
          aria-label="Next slide"
          onClick={() => {
            onInteract?.();
            onNext();
          }}
          sx={{
            border: `1px solid ${ui.borderLight}`,
            bgcolor: ui.surface,
            "&:hover": { bgcolor: ui.panelMuted },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap>
        {Array.from({ length: total }).map((_, i) => (
          <Box
            key={i}
            component="button"
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => {
              onInteract?.();
              onGoTo(i);
            }}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              border: "none",
              p: 0,
              cursor: "pointer",
              bgcolor: i === index ? ui.gold : ui.borderLight,
              transition: "transform 0.15s",
              "&:hover": { transform: "scale(1.15)" },
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function ImageCarousel({ images, ui }) {
  const resetKey = images.join("|");
  const { index, setIndex, go, pause, resume } = useCarouselIndex(images.length, resetKey);

  if (!images.length) {
    return (
      <Typography sx={{ fontSize: 13, color: ui.inkSoft, py: 2, textAlign: "center" }}>No property images</Typography>
    );
  }

  return (
    <Box onMouseEnter={pause} onMouseLeave={resume}>
      <GallerySlot
        src={images[index]}
        alt={`Property image ${index + 1}`}
        ui={ui}
        sx={{
          width: "100%",
          borderRadius: 0,
          borderLeft: "none",
          borderRight: "none",
          minHeight: { xs: 240, sm: 300, md: 380 },
          height: { xs: 240, sm: 300, md: 380 },
        }}
      />
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 0.5 }}>
        <CarouselControls
          index={index}
          total={images.length}
          onPrev={() => go(-1)}
          onNext={() => go(1)}
          onGoTo={setIndex}
          onInteract={pause}
          ui={ui}
        />
      </Box>
    </Box>
  );
}

function DocumentCarousel({ docs, ui }) {
  const resetKey = docs.map((d) => `${d.id}:${d.url || ""}`).join("|");
  const { index, setIndex, go, pause, resume } = useCarouselIndex(docs.length, resetKey);
  const current = docs[index];

  return (
    <Box onMouseEnter={pause} onMouseLeave={resume}>
      <Box sx={{ minHeight: { xs: 220, md: 280 }, height: { md: 280 } }}>
        <DocumentCard label={current.label} url={current.url} ui={ui} />
      </Box>
      <CarouselControls
        index={index}
        total={docs.length}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onGoTo={setIndex}
        onInteract={pause}
        ui={ui}
      />
    </Box>
  );
}

function FeatureCell({ icon: Icon, label, value, ui, showDivider }) {
  return (
    <Box
      sx={{
        px: { xs: 1.25, sm: 1.75 },
        py: 1.5,
        borderRight: showDivider ? { md: `1px solid ${ui.borderLight}` } : "none",
        borderBottom: { xs: showDivider ? `1px solid ${ui.borderLight}` : "none", md: "none" },
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 11, color: ui.inkSoft, mb: 0.75, letterSpacing: "0.02em" }}>{label}</Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Icon sx={{ fontSize: 18, color: ui.inkMuted, flexShrink: 0 }} />
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, color: ui.ink, lineHeight: 1.3 }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}

export default function AnnouncementDetailPanel({ announcement }) {
  const { ui, statusChipSx, tableCardSx } = useAdminUi();
  const a = announcement;
  if (!a || typeof a !== "object") return null;

  const owner = typeof a.user_id === "object" && a.user_id !== null ? a.user_id : null;
  const media = a.propertyMedia;
  const docs = a.property_documents;
  const gallery = collectGalleryImages(media);
  const docSlots = buildDocSlots(docs);
  const docWithFiles = docSlots.filter((d) => d.url).length;
  const statusLabel = formatAnnouncementStatus(a);
  const priceText = formatPropertyPrice(a.price, a.currency);
  const locationLine = [a.property_city, a.property_area].filter(Boolean).join(" / ") || a.property_country || "—";
  const fullAddress =
    a.property_address ||
    [a.property_area, a.property_city, a.property_country].filter(Boolean).join(", ") ||
    "—";

  const features = [
    { icon: CalendarTodayOutlinedIcon, label: "Listed", value: yearFromDate(a.created_at) },
    { icon: LayersOutlinedIcon, label: "Floors", value: formatFloors(a.floor, a.total_floors) },
    { icon: SquareFootOutlinedIcon, label: "Area", value: formatArea(a.property_size) },
    { icon: CategoryOutlinedIcon, label: "Property type", value: a.property_type || "—" },
    { icon: BadgeOutlinedIcon, label: "Listing", value: listingTypeLabel(a.listing_type) },
    ...(a.is_wishlisted === true || a.is_wishlisted === false
      ? [
          {
            icon: FavoriteBorderOutlinedIcon,
            label: "Wishlisted",
            value: a.is_wishlisted === true ? "Yes" : "No",
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={2.5}>
      <Paper elevation={0} sx={{ ...tableCardSx, p: { xs: 1.5, sm: 2, md: 2.5 }, overflow: "hidden" }}>
        <Box
          sx={{
            mx: { xs: -1.5, sm: -2, md: -2.5 },
            mt: { xs: -1.5, sm: -2, md: -2.5 },
            mb: 2.5,
          }}
        >
          <ImageCarousel images={gallery} ui={ui} />
          {media?.videos ? (
            <Box sx={{ mt: 1.25, textAlign: "center", px: { xs: 1.5, sm: 2 } }}>
              <Link href={media.videos} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, fontWeight: 600 }}>
                Open property video
              </Link>
            </Box>
          ) : null}
        </Box>

        <Typography sx={{ fontSize: 15, fontWeight: 600, color: ui.ink, mb: 2 }}>Announcement Details</Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1.25 }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 700,
              color: ui.inkStrong,
              lineHeight: 1.25,
              flex: 1,
              minWidth: 0,
            }}
          >
            {a.property_name || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: ui.inkSoft,
              flexShrink: 0,
              pt: { sm: 0.5 },
              textAlign: { xs: "left", sm: "right" },
            }}
          >
            {locationLine}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 2 }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 18, color: ui.inkSoft, mt: 0.15, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, color: ui.inkMuted, lineHeight: 1.45 }}>{fullAddress}</Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
            border: `1px solid ${ui.borderLight}`,
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
          }}
        >
          {features.map((f, i) => (
            <FeatureCell
              key={f.label}
              icon={f.icon}
              label={f.label}
              value={f.value}
              ui={ui}
              showDivider={i < features.length - 1}
            />
          ))}
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-end" }}
          justifyContent="space-between"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip label={statusLabel} size="small" sx={{ ...statusChipSx(statusLabel), height: 26, fontWeight: 600 }} />
            <Chip label={listingTypeLabel(a.listing_type)} size="small" variant="outlined" sx={{ height: 26 }} />
            {a.is_commercial_property ? (
              <Chip
                label="Commercial"
                size="small"
                sx={{ height: 26, bgcolor: ui.chipCommercialBg, color: ui.chipCommercialColor }}
              />
            ) : null}
            {a.propertyStatus ? (
              <Chip label={String(a.propertyStatus)} size="small" variant="outlined" sx={{ height: 26 }} />
            ) : null}
          </Stack>
          <Box sx={{ textAlign: { xs: "left", sm: "right" }, ml: { sm: "auto" } }}>
            <Stack direction="row" alignItems="center" justifyContent={{ xs: "flex-start", sm: "flex-end" }} spacing={0.5} sx={{ mb: 0.5 }}>
              <StarRoundedIcon sx={{ fontSize: 16, color: "#e8b84a" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: ui.inkMuted }}>{statusLabel}</Typography>
            </Stack>
            <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 700, color: ui.inkStrong, lineHeight: 1.2 }}>
              {priceText}
            </Typography>
            {a.brokkerage_percent != null ? (
              <Typography sx={{ fontSize: 12, color: ui.inkSoft, mt: 0.25 }}>
                Brokerage {a.brokkerage_percent}%
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...tableCardSx, p: { xs: 1.5, sm: 2 } }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: ui.ink, mb: 1.5 }}>Listed by</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Avatar src={owner?.userProfileImage || undefined} sx={{ width: 44, height: 44 }}>
            {(owner?.name || "?")[0]}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: ui.inkStrong }}>{owner?.name || "—"}</Typography>
            <Typography sx={{ fontSize: 12, color: ui.inkMuted }}>{owner?.email || "—"}</Typography>
          </Box>
        </Stack>

        {(a.bedrooms != null || a.bathrooms != null) && (
          <Typography sx={{ fontSize: 12, color: ui.inkMuted, mb: 1.5 }}>
            {a.bedrooms != null ? `${a.bedrooms} bed` : ""}
            {a.bedrooms != null && a.bathrooms != null ? " · " : ""}
            {a.bathrooms != null ? `${a.bathrooms} bath` : ""}
          </Typography>
        )}

        {a.description ? (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: ui.inkSoft, textTransform: "uppercase", mb: 0.75 }}>
              Description
            </Typography>
            <Typography sx={{ fontSize: 13, color: ui.inkMuted, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {a.description}
            </Typography>
          </Box>
        ) : null}

        <Divider sx={{ my: 1.5, borderColor: ui.borderLight }} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 12, color: ui.inkSoft }}>Created: {formatDetailDate(a.created_at)}</Typography>
          <Typography sx={{ fontSize: 12, color: ui.inkSoft }}>Updated: {formatDetailDate(a.updated_at)}</Typography>
          {a.proposals_limit != null ? (
            <Typography sx={{ fontSize: 12, color: ui.inkSoft }}>Proposals limit: {a.proposals_limit}</Typography>
          ) : null}
        </Stack>
      </Paper>

      <CollapsibleSection
        title="Documents"
        countLabel={`${docWithFiles} of ${docSlots.length} files uploaded`}
        ui={ui}
      >
        <DocumentCarousel docs={docSlots} ui={ui} />
      </CollapsibleSection>
    </Stack>
  );
}
